/*
  BluFood Warenkorb-Drawer (bf F1, ersetzt Qikify Slide Cart)
  <bf-cart-drawer> aus sections/bf-cart-drawer.liquid.

  - Oeffnet sich beim Klick auf den Warenkorb im Header und nach "In den Warenkorb"
    (Expanse-Event ajaxProduct:added). Auf der Warenkorbseite bleibt alles wie gehabt.
  - Mengen, Entfernen, Zusatzprodukte und Sparpaket-Upgrade ueber die Cart-API;
    der Inhalt wird per Section Rendering API neu gerendert (eine Quelle: Liquid).
  - Aenderungen von aussen (andere Formulare, Apps) erkennt bf-cart-watch.js.
  - R61: Versand-Rabattcodes (Gratis-Versand) erscheinen als Code-Chip, die Leiste springt auf
    "gratis" und unten steht "gratis" (Attribut _bf_ship_code, siehe bf-cart-watch.js).
  - R69: Leisten (Versand, Gratis-Zugabe) laufen beim Neu-Rendern vom alten zum neuen Stand; kommt eine
    Gratis-Zugabe dazu, huepft das Bild. "Wieder hinzufuegen" erscheint nur, wenn der Kunde sie entfernt hat.
*/
import { onCartUpdate, fetchCart, shipCodeOf, shipAttrStale, creatorCodesOf, SHIP_ATTR, giftReadd, giftOptedOut } from '@bf/cart-watch'
// Liefertermin-Auswahl immer laden: Der Drawer wird per Section Rendering neu aufgebaut; ein
// <script> im neu eingesetzten HTML wird dabei nicht ausgefuehrt (leerer Warenkorb beim Seitenaufruf).
import '@bf/delivery-picker'

const root = () => ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/')
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'

/** Gleiche Signatur wie im Liquid: "key:menge," je Position + "|" + Gesamtpreis + "|" + Versand-Code */
export function signature (cart) {
  const ship = String(((cart.attributes || {})[SHIP_ATTR]) || '').trim()
  return cart.items.map((i) => `${i.key}:${i.quantity},`).join('') + '|' + cart.total_price + '|' + ship
}

class BfCartDrawer extends HTMLElement {
  connectedCallback () {
    this._onKey = this._onKey || ((e) => { if (this.isOpen) this.onKeydown(e) })
    document.addEventListener('keydown', this._onKey)
    if (this._ready) return
    this._ready = true
    this.busy = false
    this.panel = this.querySelector('.bf-cdrawer__panel')
    this.addEventListener('click', (e) => this.onClick(e))
    this.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-bf-discount-form]')
      if (!form) return
      e.preventDefault()
      const input = form.querySelector('input')
      const code = ((input && input.value) || '').trim()
      if (code) this.applyDiscount(code)
    })
  }

  disconnectedCallback () {
    document.removeEventListener('keydown', this._onKey)
  }

  get sectionId () { return this.dataset.sectionId }
  get body () { return this.querySelector('[data-bf-cart-body]') }
  get isOpen () { return this.classList.contains('is-open') }

  open (opener) {
    if (this.isOpen) return
    this.lastFocus = opener || document.activeElement
    this.hidden = false
    document.documentElement.classList.add('bf-cart-open')
    requestAnimationFrame(() => {
      this.classList.add('is-open')
      this.panel.focus({ preventScroll: true })
    })
    // Aktuellen Stand und Serverzeit holen (Liefertermin, Aenderungen aus anderen Tabs)
    fetchCart().catch(() => {})
  }

  close () {
    if (!this.isOpen) return
    this.classList.remove('is-open')
    document.documentElement.classList.remove('bf-cart-open')
    const done = () => { if (!this.isOpen) this.hidden = true }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done()
    else setTimeout(done, 320)
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true })
  }

  onKeydown (e) {
    if (e.key === 'Escape') {
      e.preventDefault()
      this.close()
      return
    }
    if (e.key !== 'Tab') return
    const items = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null)
    if (!items.length) return
    const first = items[0]
    const last = items[items.length - 1]
    if (e.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  onClick (e) {
    const t = e.target
    if (t.closest('[data-bf-cart-close]')) {
      e.preventDefault()
      this.close()
      return
    }
    const line = t.closest('.bf-cline')
    const qtyBtn = t.closest('[data-bf-qty]')
    if (qtyBtn && line) {
      const next = Math.max(0, parseInt(line.dataset.qty, 10) + parseInt(qtyBtn.dataset.bfQty, 10))
      this.change(line.dataset.key, next)
      return
    }
    if (t.closest('[data-bf-remove]') && line) {
      this.change(line.dataset.key, 0)
      return
    }
    const add = t.closest('[data-bf-add]')
    if (add) {
      this.add(add.dataset.bfAdd)
      return
    }
    const up = t.closest('[data-bf-upgrade]')
    if (up) {
      this.upgrade(up.dataset.key, up.dataset.variant)
      return
    }
    const rm = t.closest('[data-bf-discount-remove]')
    if (rm) {
      this.removeDiscount(rm.dataset.bfDiscountRemove)
      return
    }
    const readd = t.closest('[data-bf-gift-readd]')
    if (readd && !this.busy) {
      readd.disabled = true
      this.setBusy(true)
      giftReadd().catch(() => {}).finally(() => {
        this.setBusy(false)
        this.stale = false
        this.refresh()
      })
    }
  }

  /* ---------- Rabattcodes (Cart-API /cart/update.js, Parameter "discount") ---------- */

  async currentCodes () {
    const res = await fetch(root() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
    const cart = await res.json()
    // R62: Creator-Codes behalten, auch wenn sie noch nicht gelten (z. B. Prozent-Code unter dem Mindestbestellwert)
    const keep = creatorCodesOf(cart).map((c) => c.toLowerCase())
    return (cart.discount_codes || []).filter((c) => c.applicable || keep.includes(String(c.code).toLowerCase())).map((c) => c.code)
  }

  async applyDiscount (code) {
    if (this.busy) return
    let codes = []
    try { codes = await this.currentCodes() } catch (e) { codes = [] }
    const same = (c) => String(c).toLowerCase() === code.toLowerCase()
    if (codes.some(same)) {
      this.discountMessage(`Der Code „${code}“ ist bereits eingelöst.`)
      return
    }
    const data = await this.post('cart/update.js', { discount: codes.concat(code).join(',') })
    if (!data) return
    if (Array.isArray(data.discount_codes)) {
      const hit = data.discount_codes.find((c) => same(c.code))
      if (!hit || !hit.applicable) {
        await this.post('cart/update.js', { discount: codes.join(',') })
        const text = this.dataset.discountInvalid || 'Der Code „{code}“ ist ungültig oder gilt nicht für diesen Warenkorb.'
        this.discountMessage(text.replace('{code}', code), code)
      } else if (same(shipCodeOf(data)) && this.body && this.body.dataset.shipState === 'ok') {
        // R61: Versand-Rabattcode -> Drawer zeigt schon "gratis" (post() hat das Attribut gesetzt)
        // R61b: Meeresperlen-Praemie ohne Anmeldung/Abholung -> Hinweis kommt aus dem Liquid
        const ok = this.dataset.discountShipOk || 'Code „{code}“ eingelöst: Dein Versand ist gratis.'
        this.discountMessage(ok.replace('{code}', hit.code), '', true)
      }
    }
  }

  async removeDiscount (code) {
    if (this.busy) return
    let codes = []
    try { codes = await this.currentCodes() } catch (e) { codes = [] }
    const low = (v) => String(v || '').toLowerCase()
    let rest = codes.filter((c) => low(c) !== low(code))
    const payload = {}
    // R24: Creator-Code entfernt -> Creator-Angebot (z. B. Gratis-Versand) im Warenkorb nicht mehr anzeigen
    // R62: dabei auch den Prozent-Code des Angebots "kombi" entfernen
    const creator = this.body && this.body.dataset.creator
    const creator2 = this.body && this.body.dataset.creator2
    if (creator && low(creator) === low(code)) {
      if (creator2) rest = rest.filter((c) => low(c) !== low(creator2))
      payload.attributes = { _bf_creator: '', _bf_creator_offer: '', _bf_creator_code2: '', _bf_creator_min: '', _bf_creator_pct: '' }
    } else if (creator2 && low(creator2) === low(code)) {
      // Nur den Prozent-Code entfernt: Gratis-Versand bleibt
      payload.attributes = { _bf_creator_offer: 'versand', _bf_creator_code2: '', _bf_creator_min: '', _bf_creator_pct: '' }
    }
    payload.discount = rest.join(',')
    // R61: Versand-Code entfernt -> Attribut gleich mit leeren (spart eine zweite Anfrage)
    const ship = this.body && this.body.dataset.shipCode
    if (ship && String(ship).toLowerCase() === String(code).toLowerCase()) {
      payload.attributes = Object.assign({}, payload.attributes, { [SHIP_ATTR]: '' })
    }
    await this.post('cart/update.js', payload)
  }

  /** Hinweis unter dem Rabattfeld; ok = Erfolgsmeldung (gruen, ohne Fokus ins Feld) */
  discountMessage (text, value, ok) {
    const body = this.body
    if (!body) return
    const box = body.querySelector('[data-bf-discount]')
    if (box) box.open = true
    const el = body.querySelector('[data-bf-discount-error]')
    if (el) {
      el.textContent = text
      el.classList.toggle('is-ok', !!ok)
      el.setAttribute('role', ok ? 'status' : 'alert')
      el.hidden = false
    }
    const input = body.querySelector('.bf-discount__input')
    if (input && !ok) {
      if (value) input.value = value
      input.focus()
    }
  }

  setBusy (on) {
    this.busy = on
    const body = this.body
    if (!body) return
    body.classList.toggle('is-busy', on)
    body.setAttribute('aria-busy', on ? 'true' : 'false')
  }

  showError (message) {
    const el = this.body && this.body.querySelector('[data-bf-cart-error]')
    if (!el) return
    el.textContent = message || 'Das hat leider nicht geklappt. Bitte versuche es noch einmal.'
    el.hidden = false
  }

  /** POST an die Cart-API; die Antwort enthaelt den neu gerenderten Drawer */
  async post (path, payload, nested) {
    this.setBusy(true)
    try {
      const res = await fetch(root() + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(Object.assign({}, payload, { sections: [this.sectionId], sections_url: window.location.pathname }))
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data.status) {
        await this.refresh()
        this.showError(data.description || data.message)
        return null
      }
      if (data.sections && data.sections[this.sectionId]) this.renderFrom(data.sections[this.sectionId])
      else await this.refresh()
      // R61: Versand-Rabattcode ins Attribut spiegeln und Drawer damit neu rendern (Leiste, Chip, "gratis")
      if (!nested && shipAttrStale(data)) {
        const synced = await this.post('cart/update.js', { attributes: { [SHIP_ATTR]: shipCodeOf(data) } }, true)
        return synced || data
      }
      return data
    } catch (err) {
      this.showError()
      return null
    } finally {
      if (!nested) this.setBusy(false)
      if (!nested && this.stale) {
        this.stale = false
        this.refresh()
      }
    }
  }

  change (key, quantity) {
    if (this.busy) return Promise.resolve(null)
    return this.post('cart/change.js', { id: key, quantity })
  }

  add (variantId) {
    if (this.busy) return Promise.resolve(null)
    return this.post('cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] })
  }

  async upgrade (key, variantId) {
    if (this.busy) return
    const removed = await this.change(key, 0)
    if (removed) await this.add(variantId)
  }

  /** Drawer-Inhalt neu vom Server holen */
  async refresh () {
    try {
      const res = await fetch(`${root()}?sections=${encodeURIComponent(this.sectionId)}`, { credentials: 'same-origin' })
      const data = await res.json()
      if (data && data[this.sectionId]) this.renderFrom(data[this.sectionId])
    } catch (e) { /* naechster Versuch beim naechsten Update */ }
  }

  renderFrom (html) {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const fresh = doc.querySelector('[data-bf-cart-body]')
    const current = this.body
    if (!fresh || !current) return
    const scroller = current.querySelector('.bf-cdrawer__scroll')
    const scrollTop = scroller ? scroller.scrollTop : 0
    const hadFocus = current.contains(document.activeElement)
    // R69: alten Stand der Leisten merken, damit sie zum neuen Stand laufen
    const oldBars = {}
    current.querySelectorAll('[data-bf-bar]').forEach((el) => { oldBars[el.dataset.bfBar] = el.style.width })
    const oldGift = current.querySelector('[data-bf-gift]')
    const oldCount = oldGift ? parseInt(oldGift.dataset.giftCount, 10) || 0 : 0
    current.replaceWith(fresh)
    this.animate(fresh, oldBars, oldCount)
    const nextScroller = fresh.querySelector('.bf-cdrawer__scroll')
    if (nextScroller) nextScroller.scrollTop = scrollTop
    if (hadFocus || (this.isOpen && !this.contains(document.activeElement))) this.panel.focus({ preventScroll: true })
    updateBubble(parseInt(fresh.dataset.count, 10) || 0)
  }

  /** R69: Leisten vom alten zum neuen Stand laufen lassen, neue Gratis-Zugabe hervorheben */
  animate (body, oldBars, oldCount) {
    giftOffState(body)
    const gift = body.querySelector('[data-bf-gift]')
    if (gift && (parseInt(gift.dataset.giftCount, 10) || 0) > oldCount) {
      gift.classList.add('is-pop')
      setTimeout(() => gift.classList.remove('is-pop'), 900)
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const more = gift && (parseInt(gift.dataset.giftCount, 10) || 0) > oldCount
    body.querySelectorAll('[data-bf-bar]').forEach((el) => {
      const from = oldBars[el.dataset.bfBar]
      const to = el.style.width
      if (from === undefined || from === to) return
      el.style.transition = 'none'
      el.style.width = from
      void el.offsetWidth // Startwert anwenden
      el.style.transition = ''
      if (el.dataset.bfBar === 'gift' && more && parseFloat(to) <= parseFloat(from)) {
        // Schwelle ueberschritten: erst voll laufen, dann von vorn bis zum neuen Stand
        requestAnimationFrame(() => { el.style.width = '100%' })
        setTimeout(() => {
          el.style.transition = 'none'
          el.style.width = '0%'
          void el.offsetWidth
          el.style.transition = ''
          requestAnimationFrame(() => { el.style.width = to })
        }, 450)
        return
      }
      requestAnimationFrame(() => { el.style.width = to })
    })
  }
}

/** R69: Hat der Kunde die Gratis-Zugabe entfernt, Hinweis "entfernt" und "wieder hinzufuegen" zeigen */
function giftOffState (body) {
  const readd = body && body.querySelector('[data-bf-gift-readd]')
  if (!readd) return
  const off = giftOptedOut()
  readd.hidden = !off
  const gift = body.querySelector('[data-bf-gift]')
  const text = gift && gift.querySelector('[data-bf-gift-text]')
  if (!text) return
  if (!text.dataset.onText) text.dataset.onText = text.textContent
  text.textContent = off ? (gift.dataset.offText || text.dataset.onText) : text.dataset.onText
  gift.classList.toggle('is-off', off)
}

function updateBubble (count) {
  document.querySelectorAll('.cart-link__bubble-num').forEach((el) => { el.textContent = count })
  document.querySelectorAll('.cart-link__bubble').forEach((el) => el.classList.toggle('cart-link__bubble--visible', count > 0))
}

if (!customElements.get('bf-cart-drawer')) customElements.define('bf-cart-drawer', BfCartDrawer)

const getDrawer = () => document.querySelector('bf-cart-drawer')

function setup () {
  if (!getDrawer() || window.__bfCartDrawer) return
  window.__bfCartDrawer = true
  const onCartPage = document.body.classList.contains('template-cart')

  if (!onCartPage) {
    // Expanse leitet sonst nach "In den Warenkorb" auf die Warenkorbseite weiter
    if (window.theme && window.theme.settings) window.theme.settings.cartType = 'bf-drawer'

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('#HeaderCartTrigger, [data-bf-cart-open]')
      const drawer = getDrawer()
      if (!trigger || !drawer) return
      e.preventDefault()
      e.stopImmediatePropagation()
      drawer.open(trigger)
    }, true)

    document.addEventListener('ajaxProduct:added', (e) => {
      const drawer = getDrawer()
      if (!drawer) return
      const opener = e.detail && e.detail.addToCartBtn
      drawer.refresh().then(() => drawer.open(opener))
    })
    const openEvent = () => { const d = getDrawer(); if (d) d.open() }
    document.addEventListener('cart:open', openEvent)
    document.addEventListener('bf:cart:open', openEvent)
  }

  // Aenderungen von aussen: nur neu rendern, wenn sich Positionen oder Summe geaendert haben
  onCartUpdate((cart) => {
    updateBubble(cart.item_count)
    const drawer = getDrawer()
    const body = drawer && drawer.body
    if (!body) return
    giftOffState(body)
    if (body.dataset.signature === signature(cart)) return
    // Waehrend einer eigenen Anfrage nicht dazwischenfunken, danach nachholen
    if (drawer.busy) drawer.stale = true
    else drawer.refresh()
  })

  // Theme-Editor: Section auswaehlen = Drawer oeffnen
  document.addEventListener('shopify:section:select', (e) => {
    const drawer = getDrawer()
    if (drawer && e.detail && e.detail.sectionId === drawer.sectionId) drawer.open()
  })
  document.addEventListener('shopify:section:deselect', (e) => {
    const drawer = getDrawer()
    if (drawer && e.detail && e.detail.sectionId === drawer.sectionId) drawer.close()
  })

  window.BF = window.BF || {}
  window.BF.cartDrawer = {
    open: () => { const d = getDrawer(); if (d) d.open() },
    close: () => { const d = getDrawer(); if (d) d.close() },
    refresh: () => { const d = getDrawer(); return d ? d.refresh() : Promise.resolve() }
  }
}

setup()
// Theme-Editor: Section neu geladen (z. B. Drawer eingeschaltet)
document.addEventListener('shopify:section:load', () => setup())
